import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../../Environments/Environment";
import { MaterialListItem } from "../Dto/MaterialModel";
import { CreateMaterialRequest } from "../Dto/MaterialCreateRequest";
import { UpdateMaterialRequest } from "../Dto/MaterialUpdateRequest";
import { SearchMaterialRequest } from "../Dto/MaterialSearchRequest";
import { SearchMaterialResponse } from "../Dto/MaterialSearchResponse";

@Injectable({ providedIn: "root" })
export class MaterialApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.adminServiceUrl;

  async searchMaterials(params: SearchMaterialRequest): Promise<SearchMaterialResponse> {
    const url = new URL("materials", `${this.baseUrl}/`);
    const searchParams = new URLSearchParams();

    if (params.search) {
      searchParams.set("search", params.search);
    }

    if (params.name) {
      searchParams.set("name", params.name);
    }

    if (params.sku) {
      searchParams.set("sku", params.sku);
    }

    if (params.page !== undefined) {
      searchParams.set("page", params.page.toString());
    }

    if (params.limit !== undefined) {
      searchParams.set("limit", params.limit.toString());
    }

    if (params.createdAtFrom) {
      searchParams.set("createdAtFrom", params.createdAtFrom);
    }

    if (params.createdAtTo) {
      searchParams.set("createdAtTo", params.createdAtTo);
    }

    if (searchParams.toString()) {
      url.search = searchParams.toString();
    }

    return await firstValueFrom(this.http.get<SearchMaterialResponse>(url.toString(), { withCredentials: true }));
  }

  async createMaterial(payload: CreateMaterialRequest): Promise<MaterialListItem> {
    const url = new URL("materials", `${this.baseUrl}/`);
    return await firstValueFrom(this.http.post<MaterialListItem>(url.toString(), payload, { withCredentials: true }));
  }

  async updateMaterial(materialId: string, payload: UpdateMaterialRequest): Promise<MaterialListItem> {
    const url = new URL(`materials/${materialId}`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.put<MaterialListItem>(url.toString(), payload, { withCredentials: true }));
  }

  async deleteMaterial(materialId: string): Promise<void> {
    const url = new URL(`materials/${materialId}`, `${this.baseUrl}/`);
    await firstValueFrom(this.http.delete<void>(url.toString(), { withCredentials: true }));
  }

  async updateMaterialImage(materialId: string, imageUrl: string): Promise<MaterialListItem> {
    const url = new URL(`materials/${materialId}/image`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.put<MaterialListItem>(url.toString(), { imageUrl }, { withCredentials: true }));
  }

  async deleteMaterialImage(materialId: string): Promise<void> {
    const url = new URL(`materials/${materialId}/image`, `${this.baseUrl}/`);
    await firstValueFrom(this.http.delete<void>(url.toString(), { withCredentials: true }));
  }
}
